// ActiveClinicEvents.js
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import { db } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";
import styles from "./ActiveClinicEvents.module.css";
import { doc, deleteDoc } from "firebase/firestore";
import { updateDoc } from "firebase/firestore";

const formatTime = (timeStr) => {
  const [hour, minute] = timeStr.split(":");
  const hourNum = parseInt(hour, 10);
  const ampm = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum % 12 || 12;
  return `${hour12}:${minute} ${ampm}`;
};



const ActiveClinicEvents = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState("All");
  const [eventToDelete, setEventToDelete] = useState(null);
const [showConfirmModal, setShowConfirmModal] = useState(false);
const [showSuccessPopup, setShowSuccessPopup] = useState(false);
const [eventBeingEdited, setEventBeingEdited] = useState(null);
const [showEditModal, setShowEditModal] = useState(false);



  useEffect(() => {
    const unsub = onSnapshot(collection(db, "clinic_events"), (snapshot) => {
      const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllEvents(events);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const dateStr = selectedDate ? selectedDate.toISOString().split("T")[0] : null;

    const filtered = allEvents.filter(event => {
      const matchesDate = !dateStr || event.date === dateStr;
      const matchesCohort =
        selectedCohort === "All" || event.cohorts?.includes(selectedCohort);
      return matchesDate && matchesCohort;
    });

    setFilteredEvents(filtered);
  }, [selectedDate, selectedCohort, allEvents]);

  const confirmDeleteEvent = async () => {
  try {
    await deleteDoc(doc(db, "clinic_events", eventToDelete));
    setShowConfirmModal(false);
    setEventToDelete(null);
    setShowSuccessPopup(true);
    setTimeout(() => setShowSuccessPopup(false), 3000);
  } catch (error) {
    console.error("Failed to delete event:", error);
    alert("Something went wrong.");
  }
};

const handleEmailStudents = (event) => {
  if (!event.signedUp || event.signedUp.length === 0) {
    alert("No students signed up for this event.");
    return;
  }

  // Assume signedUp is an array of emails
  const emails = event.signedUp;
  const subject = encodeURIComponent(`Clinic Event: ${event.title}`);
  const body = encodeURIComponent(
    `You're signed up for ${event.title} on ${event.date}.\n\nLocation: ${event.location}\nTime: ${event.startTime} – ${event.endTime}\nNotes: ${event.notes || "None"}`
  );

  window.location.href = `mailto:${emails.join(",")}?subject=${subject}&body=${body}`;
};

 return (
  <>
    <div className={styles.container}>
      <h2 className={styles.header}>📋 Active Clinic Events</h2>

      {/* 🔽 Cohort Filter */}
<div className={styles.cohortSelector}>
  <label htmlFor="cohort-select">🎓 Select Cohort:</label>
  <select
    id="cohort-select"
    value={selectedCohort}
    onChange={(e) => setSelectedCohort(e.target.value)}
    className={styles.dropdown}
  >
    <option value="All">All</option>
    <option value="OD-1">OD-1</option>
    <option value="OD-2">OD-2</option>
    <option value="OD-3">OD-3</option>
    <option value="OD-4">OD-4</option>
  </select>
</div>


      {/* 🔄 Flex Row */}
      <div className={styles.mainContent}>
        {/* 📅 Left - Calendar */}
        <div className={styles.calendarPane}>
          <Calendar
            className={styles.bigCalendar}
            onClickDay={setSelectedDate}
            value={selectedDate}
            tileContent={({ date, view }) => {
              const dateStr = date.toISOString().split("T")[0];
              const matchingEvents = allEvents.filter(event => event.date === dateStr);
              if (view === "month" && matchingEvents.length > 0) {
                return (
                  <div className={styles.eventDotContainer}>
                    {matchingEvents.map((_, idx) => (
                      <div key={idx} className={styles.eventDot} />
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        {/* 📦 Right - Event Cards */}
        <div className={styles.cardPane}>
          {filteredEvents.length === 0 ? (
            <p>No events match your filters.</p>
          ) : (
            filteredEvents.map((event) => {
              const signedUp = event.signedUp || [];
              const spotsLeft = event.maxStudents - signedUp.length;

              return (
                <div key={event.id} className={styles.eventCard}>
                  {/* 🔴 Cancel Button - Upper Right */}
                  <div className={styles.cancelButtonWrapper}>
  <button
    onClick={() => {
      setEventBeingEdited(event);     // ← Save this event's data
      setShowEditModal(true);         // ← Trigger the modal
    }}
    className={styles.editButton}
  >
    ✏️ Edit
  </button>
  <button
    onClick={() => {
      setEventToDelete(event.id);
      setShowConfirmModal(true);
    }}
    className={styles.cancelButton}
  >
    🗑️ Cancel Event
  </button>
</div>


                  {/* 📋 Event Details */}
                  <h3>{event.title}</h3>
                  <p><strong>📍 Location:</strong> {event.location}</p>
                  <p><strong>📅 Date:</strong> {event.date}</p>
                  <p><strong>🕒 Time:</strong> {formatTime(event.startTime)} – {formatTime(event.endTime)}</p>
                  <p><strong>🎯 Cohorts:</strong> {event.cohorts?.join(", ")}</p>
                  <p><strong>👥 Max Students:</strong> {event.maxStudents}</p>
                  <div className={styles.spotsRow}>
  <span className={`${styles.spotBadge} ${styles.filled}`}>
    {signedUp.length} Filled
  </span>
  <span className={`${styles.spotBadge} ${styles.left}`}>
    {spotsLeft} Left
  </span>
</div>

                  {event.notes && <p><strong>🧾 Notes:</strong> {event.notes}</p>}
                  <p><strong>🧑‍🎓 Students Signed Up:</strong> {signedUp.length > 0 ? signedUp.join(", ") : "No students signed up yet"}</p>
                  {signedUp.length > 0 && (
  <div className={styles.emailButtonWrapper}>
    <button
      className={styles.emailButton}
      onClick={() => handleEmailStudents(event)}
    >
      📧 Email Students
    </button>
  </div>
)}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>

    {/* ❗ Confirm Delete Modal */}
    {showConfirmModal && (
      <div className={styles.modalBackdrop}>
        <div className={styles.modalContent}>
          <h3>Are you sure you want to cancel this event?</h3>
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowConfirmModal(false)}
              style={{
                backgroundColor: "#ccc",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer"
              }}
            >
              No
            </button>
            <button
              onClick={confirmDeleteEvent}
              style={{
                backgroundColor: "#d9534f",
                color: "#fff",
                padding: "0.5rem 1rem",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer"
              }}
            >
              Yes, Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ✏️ Edit Event Modal */}
{showEditModal && eventBeingEdited && (
  <div className={styles.modalBackdrop}>
    <div className={styles.modalContent}>
      <h3>Edit Clinic Event</h3>

      <label>Title</label>
      <input
        type="text"
        value={eventBeingEdited.title}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, title: e.target.value })
        }
      />

      <label>Location</label>
      <input
        type="text"
        value={eventBeingEdited.location}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, location: e.target.value })
        }
      />

      <label>Address</label>
<input
  type="text"
  value={eventBeingEdited.address || ""}
  onChange={(e) =>
    setEventBeingEdited({ ...eventBeingEdited, address: e.target.value })
  }
/>


      <label>Start Time</label>
      <input
        type="time"
        value={eventBeingEdited.startTime}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, startTime: e.target.value })
        }
      />

      <label>End Time</label>
      <input
        type="time"
        value={eventBeingEdited.endTime}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, endTime: e.target.value })
        }
      />

      <label>Max Students</label>
      <input
        type="number"
        value={eventBeingEdited.maxStudents}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, maxStudents: parseInt(e.target.value, 10) })
        }
      />

      <label>Notes</label>
      <textarea
        rows={3}
        value={eventBeingEdited.notes || ""}
        onChange={(e) =>
          setEventBeingEdited({ ...eventBeingEdited, notes: e.target.value })
        }
      />

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1rem" }}>
        <button
          onClick={() => setShowEditModal(false)}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#ccc",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          Cancel
        </button>

        <button
          onClick={async () => {
            const docRef = doc(db, "clinic_events", eventBeingEdited.id);
            try {
              await updateDoc(docRef, eventBeingEdited);
              setShowEditModal(false);
            } catch (error) {
              console.error("Failed to update event:", error);
              alert("Update failed.");
            }
          }}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#115680",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer"
          }}
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
)}


    {/* ✅ Success Popup */}
    {showSuccessPopup && (
      <div className={styles.successPopup}>
        ✅ Clinic event successfully cancelled.
      </div>
    )}
  </>
);
};

export default ActiveClinicEvents;
